// Need to use the React-specific entry point to import createApi
import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError
} from '@reduxjs/toolkit/query/react';
import { authHeaderToken } from './auth-header';
import {getCurrentUser, logoutUser} from './helper';
import { Middleware } from '@reduxjs/toolkit';
import { startProgress, doneProgress } from '../utils/nprogress';
import { isAdmin } from '../utils/consts';

export const baseUrl = process.env.REACT_APP_API_URL || `http://localhost:3000/api/`;

const baseQuery = fetchBaseQuery({
  baseUrl,
  prepareHeaders: (headers) => {
    const token = authHeaderToken();
    if (token) {
      headers.set('x-access-token', token);
      headers.set('Accept', 'application/json');
    }
    return headers;
  }
});

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  let result = await baseQuery(args, api, extraOptions);
  if (result.error && result.error.status === 401) {
    // Capture admin status BEFORE clearing, since logoutUser() wipes the user.
    const wasAdmin = isAdmin(getCurrentUser()?.user_type_id ?? -1);
    logoutUser();
    // Only admins are hard-redirected to landing on 401: covers a half-
    // authenticated pending-token holder hitting a protected route and expired
    // admin sessions. Non-admins are logged out in place. Hard location change
    // because this runs outside the router.
    if (wasAdmin && typeof window !== 'undefined' && window.location.pathname !== '/') {
      window.location.href = '/';
    }
  }
  return result;
};

// Define a service using a base URL and expected endpoints
export const sfcApi = createApi({
  reducerPath: 'sfcApi',
  baseQuery: baseQueryWithReauth,
  // Disable cache garbage-collection timers in test environments.
  // The default (60s) keeps the Node event loop alive and prevents Jest from exiting.
  keepUnusedDataFor: process.env.NODE_ENV === 'test' ? 0 : 60,
  tagTypes: [
    'Admins',
    'Instructors',
    'Parent',
    'Child',
    'Allergy',
    'Waiver', // people
    'Course',
    'Lesson',
    'LessonAssignments', // schedule
    'CurrentEntries',
    'TimesheetOverrides', // timesheets
    'Venue',
    'Location',
    'Organization',
    'ZipCode',
    'Territory', // places
    'Invoice',
    'InvoiceItem',
    'InvoiceClient',
    'Receipt',
    'Payment',
    'Expense',
    'Enrollment',
    'CurrentEntries',
    'PowerBI',
    'Invoicer',
    'StripeKey',
    'Intent',
    'CreatePayment', // finance
    'Order',
    'Scholarship',
    'Discount',
    'PromotionalCode',
    'Refund',
    'Waitlist', // orders
    'Recipe',
    'Documents',
    'Themes',
    'Joke',
    'JokeCategory',
    'FeaturedIngredients',
    'CountryFacts',
    'FoodHistories',
    'KitchenSkills',
    'Questions', // Content
    'Guides',
    'Steps',
    'TrainingVideos', // Training
    'NewsItem',
    'Feedback',
    'FranchiseeLead',
    'Gallery',
    'Snippet',
    'TeamProfile',
    'Page', //websites
    'Registrations',
    'Testimonial',
    'CourseLedger',
    'LocationMapGroup'
  ], //websites
  endpoints: () => ({})
});

export const progressMiddleware: Middleware = () => (next) => (action) => {
  // RTK Query pending actions have type ending in "/pending"
  if (typeof action.type === 'string') {
    if (action.type.endsWith('/executeQuery/pending') || action.type.endsWith('/executeMutation/pending')) {
      startProgress();
    } else if (
        action.type.endsWith('/executeQuery/fulfilled') ||
        action.type.endsWith('/executeQuery/rejected') ||
        action.type.endsWith('/executeMutation/fulfilled') ||
        action.type.endsWith('/executeMutation/rejected')
    ) {
      doneProgress();
    }
  }
  return next(action);
};

type DownloadIndexParams = {
  method?: 'GET' | 'POST';
  format: 'json' | 'csv' | 'xlsx' | 'pdf';
  path: string;
  body?: any;
  scope?: string;
  filename?: string;
};

export const downloadIndex = async (params: DownloadIndexParams) => {
  const token = authHeaderToken();
  let headers = new Headers({
    'Content-Type': 'application/json'
  });
  if (token) {
    headers.set('x-access-token', token);
  }

  let url =
    baseUrl +
    params.path +
    `${params?.scope ? `?scope=${params.scope}` : ''}${params?.format && params?.format !== 'pdf' ? `${params?.scope ? '&' : '?'}format=${params.format}` : ''}`;
  startProgress();
  try {
    const response = await fetch(url, {
      headers,
      method: params.method ?? 'POST',
      body: params.method === 'POST' ? JSON.stringify(params.body) : undefined
    });

    // Get filename from Content-Disposition header if available
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = params.filename ?? `${params.path}.${params.format}`;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1];
      }
    }
    if (response.status !== 200) {
      return null;
    }

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } catch (error) {
    console.log(error);
  } finally {
    doneProgress();
  }

  return null;
};

export const downloadBlobFile = (blob: Blob, fileName: string) => {
  const blobUrl = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => window.URL.revokeObjectURL(blobUrl), 10000);
};

export const downloadReceiptImage = async (receiptId: number, fileName: string) => {
  const token = authHeaderToken();
  const headers = new Headers();
  if (token) {
    headers.set('x-access-token', token);
  }

  startProgress();
  try {
    const response = await fetch(`${baseUrl}receipts/${receiptId}/download-image`, { headers });
    if (!response.ok) {
      throw new Error('Failed to download receipt image');
    }

    const contentDisposition = response.headers.get('Content-Disposition');
    let resolvedName = fileName || 'receipt-image';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
      if (filenameMatch?.[1]) {
        resolvedName = filenameMatch[1];
      }
    }

    const blob = await response.blob();
    downloadBlobFile(blob, resolvedName);
  } catch (error) {
    console.error(error);
    throw error;
  } finally {
    doneProgress();
  }
};

export const buildCoursePdfFileName = (mode: string, slug?: string | null): string =>
  (`sfc_${mode}_${slug ?? ''}_${Math.floor(Date.now() / 1000)}.pdf`).replace(/-/g, '_');

export const openPdfPreview = async (pathWithQuery: string): Promise<void> => {
  // Open the tab synchronously while still inside the click handler so browsers
  // don't treat it as a blocked popup after the async PDF fetch completes.
  const previewWindow = window.open('about:blank', '_blank');
  if (!previewWindow) {
    throw new Error('Please allow pop-ups to preview the PDF');
  }

  try {
    previewWindow.document.title = 'Preparing PDF…';
    previewWindow.document.body.innerHTML =
      '<p style="font-family:sans-serif;padding:2rem;">Preparing PDF…</p>';

    const token = authHeaderToken();
    const headers = new Headers();
    if (token) {
      headers.set('x-access-token', token);
    }

    const response = await fetch(`${baseUrl}${pathWithQuery}`, {
      headers,
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error('Failed to fetch the PDF');
    }

    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    previewWindow.location.href = blobUrl;
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  } catch (error) {
    previewWindow.close();
    throw error;
  }
};

type DownloadInvoiceParams = {
  path: string;
};
export const downloadInvoice = async (params: DownloadInvoiceParams) => {
  try {
    const token = authHeaderToken();
    let headers = new Headers({
      'Content-Type': 'application/json'
    });
    if (token) {
      headers.set('x-access-token', token);
    }

    let url = baseUrl + params.path;

    const response = await fetch(url, {
      headers
    });

    if (!response.ok) {
      throw new Error('Failed to fetch the PDF');
    }

    // Get the Blob data
    const blob = await response.blob();

    // Create a URL for the Blob and open it in a new window
    const openurl = URL.createObjectURL(blob);
    window.open(openurl, '_blank');
  } catch (error) {
    // addToast('Error while fetching the PDF: ' + error.message, 'error');
    alert('Error while fetching the PDF: ' + error.message);
  }

  return null;
};
